import { useRef, useState } from 'react';
import { FieldRow } from '@/components/common/FieldRow';
import { FormHeader } from '@/components/common/FormHeader';
import { FormStatusMessage } from '@/components/common/FormStatusMessage';
import { FormSuccessState } from '@/components/common/FormSuccessState';
import { HoneypotField } from '@/components/common/HoneypotField';
import { SubmitButton } from '@/components/common/SubmitButton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FieldLimits } from '@/constants/fieldLimits';
import { FormStatus } from '@/constants/formStatus';
import { useFormFields } from '@/hooks/useFormFields';
import { findFirstInvalidField, getFieldAriaProps, pickTouchedErrors } from '@/lib/formErrors';
import { ContactFormContent } from '@/pages/Contact/data';
import { ContactFieldIds, ContactFieldOrder, EmptyContactMessage, validateContactMessage } from '@/pages/Contact/validation';
import { createSubmissionId, submitContactMessage } from '@/services/inquiryService';

const FaxFieldId = 'contact-fax';

export function ContactForm({ titleId }) {
  const { values, touchedFields, markAllTouched, resetFields, bindInput } = useFormFields(EmptyContactMessage);
  const [formStatus, setFormStatus] = useState(FormStatus.idle);
  const [failureMessage, setFailureMessage] = useState(null);
  const [submissionId, setSubmissionId] = useState(createSubmissionId);
  const faxInputRef = useRef(null);
  const errors = validateContactMessage(values);
  const shownErrors = pickTouchedErrors(errors, touchedFields);
  const isSubmitting = formStatus === FormStatus.submitting;

  async function handleSubmit(event) {
    event.preventDefault();
    markAllTouched();
    const firstInvalidField = findFirstInvalidField(errors, ContactFieldOrder);
    if (firstInvalidField) {
      document.getElementById(ContactFieldIds[firstInvalidField])?.focus();
      return;
    }

    setFormStatus(FormStatus.submitting);
    const submissionFailure = await submitContactMessage({ ...values, submissionId, fax: faxInputRef.current?.value ?? '' });
    setFailureMessage(submissionFailure);
    setFormStatus(submissionFailure ? FormStatus.failed : FormStatus.succeeded);
  }

  function startOver() {
    resetFields();
    setFailureMessage(null);
    setSubmissionId(createSubmissionId());
    setFormStatus(FormStatus.idle);
  }

  if (formStatus === FormStatus.succeeded) {
    return (
      <FormSuccessState
        titleId={titleId}
        title={ContactFormContent.success.title}
        description={ContactFormContent.success.description}
        actionLabel={ContactFormContent.success.actionLabel}
        onAction={startOver}
      />
    );
  }

  return (
    <>
      <FormHeader titleId={titleId} title={ContactFormContent.title} />
      <form noValidate onSubmit={handleSubmit} aria-busy={isSubmitting} className="mt-8 space-y-5">
        <div className="grid gap-5 sm:grid-cols-2">
          <FieldRow id={ContactFieldIds.name} label="Name" required error={shownErrors.name}>
            <Input
              id={ContactFieldIds.name}
              autoComplete="name"
              maxLength={FieldLimits.name}
              {...bindInput('name')}
              {...getFieldAriaProps(ContactFieldIds.name, shownErrors.name)}
            />
          </FieldRow>
          <FieldRow id={ContactFieldIds.email} label="Email" required error={shownErrors.email}>
            <Input
              id={ContactFieldIds.email}
              type="email"
              inputMode="email"
              autoComplete="email"
              maxLength={FieldLimits.email}
              {...bindInput('email')}
              {...getFieldAriaProps(ContactFieldIds.email, shownErrors.email)}
            />
          </FieldRow>
          <FieldRow id={ContactFieldIds.company} label="Company">
            <Input id={ContactFieldIds.company} autoComplete="organization" maxLength={FieldLimits.company} {...bindInput('company')} />
          </FieldRow>
          <FieldRow id={ContactFieldIds.phone} label="Phone" error={shownErrors.phone}>
            <Input
              id={ContactFieldIds.phone}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              maxLength={FieldLimits.phone}
              {...bindInput('phone')}
              {...getFieldAriaProps(ContactFieldIds.phone, shownErrors.phone)}
            />
          </FieldRow>
        </div>
        <FieldRow id={ContactFieldIds.message} label="Message" required error={shownErrors.message}>
          <Textarea
            id={ContactFieldIds.message}
            placeholder={ContactFormContent.messagePlaceholder}
            maxLength={FieldLimits.message}
            {...bindInput('message')}
            {...getFieldAriaProps(ContactFieldIds.message, shownErrors.message)}
          />
        </FieldRow>
        <HoneypotField id={FaxFieldId} inputRef={faxInputRef} />
        <FormStatusMessage message={formStatus === FormStatus.failed ? failureMessage : null} />
        <div className="flex flex-col gap-4 pt-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-steel">{ContactFormContent.privacyNote}</p>
          <SubmitButton
            isSubmitting={isSubmitting}
            label={ContactFormContent.submitLabel}
            submittingLabel={ContactFormContent.submittingLabel}
            className="w-full sm:w-auto"
          />
        </div>
      </form>
    </>
  );
}